import type { ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, viewChild } from '@angular/core';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { icon as renderFaIcon, type IconLookup } from '@fortawesome/fontawesome-svg-core';
import { downgradeComponent } from '@angular/upgrade/static';
import App from '@app/app';
import { parseFaIcon, type IconName, type LegacyIconName } from './lt-icon.config';
import { CUSTOM_ICON_SVG } from './lt-icon.custom-icons';

/**
 * Icon SVG is written imperatively from an `effect()` (the effect-flush phase),
 * never via a template `[innerHTML]` binding or the `<fa-icon>` host binding.
 *
 * Why: the Transcend consent manager (airgap.js) monkey-patches the DOM
 * `innerHTML` setter and re-enters Angular's reactivity synchronously. When that
 * write happens during template rendering it triggers a signal write mid-render
 * (NG0600) — which surfaced on route transitions as new icons first rendered.
 * Effects run outside the render context, so the same write is allowed there.
 */
@Component({
  selector: 'lt-icon',
  template: `
    <span #defaultGlyph class="lt-icon__glyph lt-icon__glyph--default"></span>
    @if (hoverName()) {
      <span #hoverGlyph class="lt-icon__glyph lt-icon__glyph--hover"></span>
    }
  `,
  host: {
    '[class]': 'hostClass()',
    '[attr.role]': '"img"',
    '[attr.aria-label]': 'ariaLabel() || null'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LtIconComponent {
  private readonly faLibrary = inject(FaIconLibrary);

  name = input.required<IconName>();
  hoverName = input<IconName>();
  ariaLabel = input<string>();

  private readonly defaultGlyph = viewChild<ElementRef<HTMLElement>>('defaultGlyph');
  private readonly hoverGlyph = viewChild<ElementRef<HTMLElement>>('hoverGlyph');

  protected faRef = computed(() => parseFaIcon(this.name()));

  protected hostClass = computed(() => {
    const base = this.faRef() ? 'lt-icon lt-icon--fa' : `lt-icon lt-icon--${this.name()}`;
    return this.hoverName() ? `${base} lt-icon--has-hover` : base;
  });

  private readonly defaultSvg = computed(() => this.resolveSvg(this.name()));
  private readonly hoverSvg = computed(() => {
    const hover = this.hoverName();
    return hover ? this.resolveSvg(hover) : '';
  });

  constructor() {
    effect(() => this.paint(this.defaultGlyph(), this.defaultSvg()));
    effect(() => this.paint(this.hoverGlyph(), this.hoverSvg()));
  }

  private paint(ref: ElementRef<HTMLElement> | undefined, svg: string): void {
    // Safe: svg is either FontAwesome-rendered markup or a build-time webpack
    // `?raw` SVG import — never user-controlled content.
    if (ref) ref.nativeElement.innerHTML = svg;
  }

  private resolveSvg(name: IconName): string {
    const ref = parseFaIcon(name);
    if (ref) {
      // getIconDefinition returns a full IconDefinition; icon() renders it
      // directly (its type only advertises IconLookup, hence the cast).
      const definition = this.faLibrary.getIconDefinition(ref[0], ref[1]);
      return definition ? renderFaIcon(definition as IconLookup).html.join('') : '';
    }
    const customIcons: Partial<Record<LegacyIconName, string>> = CUSTOM_ICON_SVG;
    return customIcons[name as LegacyIconName] ?? '';
  }
}

App.directive('ltIcon', downgradeComponent({ component: LtIconComponent }));
